import { CompanyService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useFunctionRulesStore } from './functionRulesStore';
import type {
  Function as CompanyFunction,
  CreateFunctionRequest,
  UpdateFunctionRequest,
} from '@beeboard/common';

export type FunctionStoreState = {
  functions: CompanyFunction[];
};

export const useFunctionStore = defineStore('functionStore', {
  state: (): FunctionStoreState => ({
    functions: [] as CompanyFunction[],
  }),

  getters: {
    function(state) {
      return (functionId: string): CompanyFunction => {
        const fun = state.functions.find((fn) => fn.id === functionId);
        if (!fun) {
          throw new Error(`Missing function ${functionId} in function store`);
        }

        return fun;
      };
    },
    hasFunction(state) {
      return (functionId: string): boolean => {
        return state.functions.some((fn) => fn.id === functionId);
      };
    },
  },

  actions: {
    write(fn: CompanyFunction) {
      const functions = this.$state.functions;
      const oldFn = functions.find((f) => f.id === fn.id);
      if (oldFn) {
        Object.assign(oldFn, fn);
      } else {
        functions.push(fn);
      }
    },

    async fetchAll() {
      const functions = await CompanyService.getFunctions();
      functions.sort((a, b) => a.name.localeCompare(b.name));
      this.$patch({ functions });
    },

    async create(payload: CreateFunctionRequest) {
      const errors =
        useFunctionRulesStore().validateCreateFunctionRequest(payload);
      if (errors) {
        throw errors;
      }
      const fn = await CompanyService.createFunction(payload);
      this.write(fn);
    },

    async update(fnId: string, payload: UpdateFunctionRequest) {
      const errors = useFunctionRulesStore().validateUpdateFunctionRequest(
        payload,
        fnId,
      );
      if (errors) {
        throw errors;
      }
      const fn = await CompanyService.updateFunction(fnId, payload);
      this.write(fn);
    },

    async delete(fnId: string) {
      const errors =
        useFunctionRulesStore().validateRemoveFunctionRequest(fnId);
      if (errors) {
        throw errors;
      }
      await CompanyService.deleteFunction(fnId);
      const functions = this.$state.functions;
      const fnIndex = functions.findIndex((f) => f.id === fnId);
      if (fnIndex > -1) {
        functions.splice(fnIndex, 1);
      }
    },
  },
});
